// Review & pay. Same two-phase flow as the flight Payment page:
//
//   1. Pay → POST /reprice/
//      a. price unchanged → cascade straight into POST /book/
//      b. price changed   → confirmation modal with old vs new; Accept runs /book/
//   2. /book/ runs behind a full-screen loader, then we land on confirmation.
//      402 ("insufficient balance") surfaces inline with the exact shortfall.
//
// What differs from flights is what /book/ is doing while the loader is up. A
// hotel offer can't be held, so nothing is reserved until this moment: the call
// re-searches, re-matches the same room, reconciles the price, reserves it,
// reconciles again, then books. The loader steps name that honestly rather than
// pretending a room has been sitting held since the guests page.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBodyClass } from "../lib/useBodyClass";
import {
  fmtBoard, fmtTaxDescription, fmtDateFull, fmtDeadline, fmtMoney, nightsLabel, occupancyLabel, starLabel,
} from "../lib/hotelFormat";
import { BookingLoader } from "../components/BookingLoader";
import { HotelFlowStepper } from "./HotelGuests";
import type { HotelItinerary } from "../lib/hotelTypes";

type Phase = "idle" | "repricing" | "awaiting-confirm" | "booking";

export function HotelPayment() {
  useBodyClass("has-bg2");
  const { bookingId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { me, refresh } = useAuth();
  const partner = me?.partner;

  const bookingQ = useQuery({
    queryKey: ["hotelBooking", bookingId],
    queryFn: () => api.get<HotelItinerary>(`/v1/hotels/booking/${bookingId}/`),
    enabled: !!bookingId,
  });

  const [phase, setPhase] = useState<Phase>("idle");
  const [priced, setPriced] = useState<HotelItinerary | null>(null);
  // /book/ is one HTTP call but three real steps upstream. We advance the
  // visible step on a timer so the loader tracks work that is genuinely
  // happening instead of flashing through to success.
  const [bookSubStep, setBookSubStep] = useState<0 | 1>(0);
  useEffect(() => {
    if (phase !== "booking") { setBookSubStep(0); return; }
    setBookSubStep(0);
    const t = window.setTimeout(() => setBookSubStep(1), 2200);
    return () => window.clearTimeout(t);
  }, [phase]);

  const reprice = useMutation({
    mutationFn: () => api.post<HotelItinerary>(`/v1/hotels/booking/${bookingId}/reprice/`),
    onSuccess: (data) => {
      if (bookingId) qc.setQueryData(["hotelBooking", bookingId], data);
      setPriced(data);
      if (data.pricing.price_changed) {
        setPhase("awaiting-confirm");
      } else {
        setPhase("booking");
        book.mutate();
      }
    },
    onError: () => setPhase("idle"),
  });

  const book = useMutation({
    mutationFn: () => api.post<HotelItinerary>(`/v1/hotels/booking/${bookingId}/book/`),
    onSuccess: async (data) => {
      // Seed the cache with the BOOK response. Without this the confirmation
      // page reuses this page's earlier GET — which is the pre-book quote and
      // carries no references — and renders "Pending" for a confirmed stay,
      // because React Query's staleTime means it won't refetch for 30s.
      if (bookingId) qc.setQueryData(["hotelBooking", bookingId], data);
      // The wallet has just been debited; re-read the partner so the nav chip
      // isn't showing a balance from before the booking.
      await refresh();
      nav(`/booking/${bookingId}/confirmation`);
    },
    onError: () => setPhase("idle"),
  });

  function onPay() {
    if (phase !== "idle") return;
    setPhase("repricing");
    reprice.mutate();
  }

  if (bookingQ.isLoading) return <div className="app-loading"><span className="spinner" />Loading…</div>;
  if (bookingQ.error || !bookingQ.data) return <div className="app-error">Couldn't load booking.</div>;

  const b: HotelItinerary = priced ?? bookingQ.data;
  const { hotel, room, pricing } = b;
  const currency = pricing.currency;
  const stars = starLabel(hotel.star_rating);
  const propertyLabel = hotel.name || "the property";

  const bookError = book.error instanceof ApiError ? book.error : null;
  const repriceError = reprice.error instanceof ApiError ? reprice.error : null;
  const insufficient = bookError?.status === 402;
  const errBody = (bookError?.body || {}) as { required_inr?: number; balance_inr?: number };

  const stateFor = (target: number): "done" | "active" | "pending" =>
    bookSubStep > target ? "done" : bookSubStep === target ? "active" : "pending";
  const loaderSteps =
    phase === "repricing" ? [
      { label: `Re-checking the rate with ${propertyLabel}`, state: "active" as const },
      { label: "Reserving your room", state: "pending" as const },
      { label: "Debiting wallet & confirming", state: "pending" as const },
    ]
    : phase === "booking" ? [
      { label: `Rate confirmed with ${propertyLabel}`, state: "done" as const },
      { label: "Reserving your room", state: stateFor(0) },
      { label: `Debiting ${fmtMoney(pricing.final_fare, currency)} & confirming`, state: stateFor(1) },
    ]
    : null;
  const showLoader = phase === "repricing" || phase === "booking";

  return (
    <>
      {showLoader && loaderSteps && (
        <BookingLoader airlineLabel={propertyLabel} steps={loaderSteps} />
      )}

      <HotelFlowStepper current="payment" />

      <div className="container">
        <div className="pay-wrap">
          <h1 className="pay-h">Final review &amp; <span className="serif">pay</span>.</h1>
          <p className="pay-sub">
            Hotel rates aren't held — we re-check this one with {propertyLabel} the moment
            you tap Pay, reserve the room, and only then debit your wallet. If the rate has
            moved you'll see the new total before anything is charged.
          </p>

          <div className="payment-grid">
            {/* LEFT — the stay */}
            <div>
              <div className="itin-block">
                <div className="dir">Property</div>
                <div className="hotel-pay-head">
                  {(hotel.thumbnail || hotel.main_image) && (
                    <img className="hotel-pay-thumb" src={hotel.thumbnail || hotel.main_image || ""} alt="" />
                  )}
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>
                      {stars && <span className="hotel-stars">{stars}</span>}
                      {hotel.name}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 3 }}>
                      {[hotel.address, hotel.city_name].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="itin-block">
                <div className="dir">Stay</div>
                <div className="hotel-pay-dates">
                  <div>
                    <span className="lbl">Check-in</span>
                    <b>{fmtDateFull(b.check_in)}</b>
                    {hotel.check_in_time && <span className="t">from {hotel.check_in_time}</span>}
                  </div>
                  <div className="hotel-pay-nights">{nightsLabel(b.nights)}</div>
                  <div>
                    <span className="lbl">Check-out</span>
                    <b>{fmtDateFull(b.check_out)}</b>
                    {hotel.check_out_time && <span className="t">by {hotel.check_out_time}</span>}
                  </div>
                </div>
                <div className="itin-detail">
                  <span><b>{room.name}</b> · {fmtBoard(room.board_basis, room.board_name)}</span>
                  <span>{occupancyLabel(b.occupancies)}</span>
                  <span className={`tag ${room.refundable ? "refundable" : "non-refundable"}`}>
                    {room.refundable ? "Free cancellation" : "Non-refundable"}
                  </span>
                </div>
                <div className={`room-cancel${room.refundable ? " ok" : ""}`} style={{ marginTop: 10 }}>
                  {room.free_cancellation_until
                    ? <>Free cancellation until <b>{fmtDeadline(room.free_cancellation_until)}</b></>
                    : room.cancellation_summary}
                </div>
              </div>

              {!!b.guests?.length && (
                <div className="itin-block">
                  <div className="dir">Guests</div>
                  {b.guests.map((guest, i) => (
                    <div className="passenger-line" key={i}>
                      <div>
                        <b>
                          Room {guest.occupancy_number ?? i + 1} ·{" "}
                          {[guest.title, guest.first_name, guest.last_name].filter(Boolean).join(" ")}
                        </b>
                        <span>
                          {guest.other_occupants?.length
                            ? `with ${guest.other_occupants.map((o) => o.first_name).filter(Boolean).join(", ")}`
                            : "sole named guest"}
                          {guest.is_lead && guest.email ? ` · ${guest.email}` : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="itin-block">
                <div className="dir">Price breakdown</div>
                <div className="fare-line">
                  <span className="lbl">
                    Room rate · {nightsLabel(b.nights)}
                    {b.occupancies.length > 1 ? ` × ${b.occupancies.length} rooms` : ""}
                  </span>
                  <span>{fmtMoney(pricing.base_fare, currency)}</span>
                </div>
                <div className="fare-line total">
                  <span className="lbl">You'll pay now</span>
                  <span>{fmtMoney(pricing.final_fare, currency)}</span>
                </div>
                {!!pricing.taxes_at_property && (
                  <>
                    {/* Kept visibly outside the total: this is the property's
                        money, collected at check-in, and never touches our
                        wallet ledger. */}
                    <div className="fare-line at-property-line">
                      <span className="lbl">Payable at the property</span>
                      <span>{fmtMoney(pricing.taxes_at_property, currency)}</span>
                    </div>
                    <ul className="at-property-breakdown">
                      {pricing.taxes_at_property_breakdown.map((tax, i) => (
                        <li key={i}>
                          <span>{fmtTaxDescription(tax.description)}</span>
                          <span>{fmtMoney(tax.amount, tax.currency)}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>

            {/* RIGHT — pay box */}
            <aside>
              <div className="pay-box">
                <div className="pay-amount-block">
                  <div className="lbl">You'll pay</div>
                  <div className="amount">{fmtMoney(pricing.final_fare, currency)}</div>
                  <div className="amount-sub">debited from wallet in INR</div>
                </div>

                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-3)", marginBottom: 10 }}>
                  Payment method
                </div>
                <div className="pay-method razorpay">
                  <div className="ic ic-rzp" style={{ background: "var(--ink-rail)", color: "var(--yellow)" }}>W</div>
                  <div className="info">
                    <b>Partner wallet</b>
                    <span>
                      Balance: {partner ? <b style={{ color: "var(--ink)" }}>{fmtMoney(partner.balance, "INR")}</b> : "—"}
                      {partner && ` · ${partner.name}`}
                    </span>
                  </div>
                  <div className="check">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}><path d="M5 12l5 5L20 7" /></svg>
                  </div>
                </div>

                {repriceError && (
                  <div className="info-banner warn" style={{ margin: "14px 0 0" }}>
                    <span><b>Couldn't re-check the rate.</b> {repriceError.message}</span>
                  </div>
                )}
                {insufficient && (
                  <div className="info-banner warn" style={{ margin: "14px 0 0" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /></svg>
                    <span>
                      <b>Insufficient wallet balance.</b>{" "}
                      Need {fmtMoney(errBody.required_inr ?? 0, "INR")} · have {fmtMoney(errBody.balance_inr ?? 0, "INR")}.
                      Top up and retry.
                    </span>
                  </div>
                )}
                {bookError && !insufficient && (
                  <div className="info-banner warn" style={{ margin: "14px 0 0" }}>
                    <span>{bookError.message}</span>
                  </div>
                )}

                <button
                  type="button"
                  className={`pay-btn${showLoader ? " loading" : ""}`}
                  disabled={showLoader}
                  onClick={onPay}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 9V7a5 5 0 0 1 10 0v2M3 9h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" transform="translate(2,0)" /></svg>
                  Pay {fmtMoney(pricing.final_fare, currency)} from wallet
                </button>

                <div className="pay-securetext">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  Wallet ledgered with every debit · the room is reserved before you're charged
                </div>

                <div className="note-block">
                  <b>What happens next.</b><br />
                  We re-check the rate, reserve the room at {propertyLabel}, then debit your INR
                  wallet — usually under 30 seconds. If the rate or the cancellation terms have
                  moved, we <span className="serif">stop and ask</span> rather than booking a
                  different deal than the one you saw.
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {phase === "awaiting-confirm" && priced && (
        <RateChangedModal
          previous={priced.pricing.previous_total_amount ?? null}
          next={priced.pricing.final_fare}
          currency={currency}
          onAccept={() => { setPhase("booking"); book.mutate(); }}
          onCancel={() => { setPhase("idle"); setPriced(null); reprice.reset(); }}
        />
      )}
    </>
  );
}

function RateChangedModal(props: {
  previous: number | null;
  next: number;
  currency: string;
  onAccept: () => void;
  onCancel: () => void;
}) {
  const diff = props.previous != null ? props.next - props.previous : 0;
  return (
    <div className="drawer-overlay" onClick={props.onCancel} style={{ display: "grid", placeItems: "center" }}>
      <div
        role="dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 92vw)", background: "var(--bg)",
          borderRadius: 18, padding: 26, boxShadow: "0 24px 60px rgba(11,18,32,0.25)",
        }}
      >
        <div className="info-banner warn" style={{ margin: "0 0 14px" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /></svg>
          <span>
            <b>Rate moved {diff > 0 ? "up" : "down"} by {fmtMoney(Math.abs(diff), props.currency)}.</b>{" "}
            Hotel rates change constantly — confirm before we debit your wallet.
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 18 }}>
          {props.previous != null && (
            <span style={{ fontSize: 18, color: "var(--ink-4)", textDecoration: "line-through", fontWeight: 600 }}>
              {fmtMoney(props.previous, props.currency)}
            </span>
          )}
          <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em" }}>
            {fmtMoney(props.next, props.currency)}
          </span>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost btn-pill" onClick={props.onCancel}>
            Don't pay yet
          </button>
          <button type="button" className="btn btn-yellow btn-pill" onClick={props.onAccept}>
            Accept &amp; pay
          </button>
        </div>
      </div>
    </div>
  );
}
